import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { useShortName } from "../../../utils/useShortName";
import { getCompanyInfo } from "../../../api/company";
import { capitalizeFirstLetter, capitalizeWords } from "../../../utils/letters";
import { formatRut } from "../../../utils/rutUtils";
import PortalTopBar from "../../portal/shared/PortalTopBar";
import PortalSidebarShell from "../../portal/shared/PortalSidebarShell";
import ClassFooter from '../../student/classroom/classFooter';

export default function CompanyPortal({ footerData, isLoading: footerLoading }) {
    const navigate = useNavigate();
    const { logout, user } = useAuth();
    const shortDisplayName = useShortName();
    const [company, setCompany] = useState(null);
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [desktopSidebarHidden, setDesktopSidebarHidden] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const userMenuRef = useRef(null);

    useEffect(() => {
        document.title = "Portal Organización | Somnus";
        return () => {
            document.title = "Somnus";
        };
    }, []);

    useEffect(() => {
        let isMounted = true;
        async function fetchCompany() {
            try {
                const data = await getCompanyInfo();
                if (isMounted) setCompany(data);
            } catch (error) {
                console.error("No se pudo cargar la información de la organización", error);
            }
        }
        fetchCompany();

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!mobileSidebarOpen) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [mobileSidebarOpen]);

    useEffect(() => {
        const onKeydown = (event) => {
            if (event.key === "Escape") setMobileSidebarOpen(false);
        };
        window.addEventListener("keydown", onKeydown);
        return () => window.removeEventListener("keydown", onKeydown);
    }, []);

    useEffect(() => {
        if (!userMenuOpen) return;

        const handleClickOutside = (event) => {
            if (!userMenuRef.current?.contains(event.target)) {
                setUserMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [userMenuOpen]);

    const handleLogout = async () => {
        try {
            await logout();
        } finally {
            navigate("/login-emp", { replace: true });
        }
    };

    const toggleSidebar = () => {
        const isMobileViewport =
            typeof window !== "undefined" &&
            window.matchMedia("(max-width: 931px)").matches;

        if (isMobileViewport) {
            setMobileSidebarOpen((open) => !open);
            return;
        }

        setDesktopSidebarHidden((hidden) => !hidden);
    };

    const sidebarToggleLabel =
        mobileSidebarOpen || !desktopSidebarHidden
            ? "Ocultar panel izquierdo"
            : "Mostrar panel izquierdo";
    const isSidebarVisible = mobileSidebarOpen || !desktopSidebarHidden;

    const fullDisplayName =
        user?.fullName ||
        `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
        shortDisplayName ||
        "Representante";
    const companyUserName = capitalizeWords(fullDisplayName);
    const userInitial = companyUserName.charAt(0).toUpperCase();

    return (
        <div className="student-portal-container teacher-portal-layout">
            <PortalSidebarShell
                portalSubtitle="Portal Organización"
                userLabel="Representante conectado"
                userName={companyUserName}
                mobileSidebarOpen={mobileSidebarOpen}
                desktopSidebarHidden={desktopSidebarHidden}
                sidebarToggleLabel={sidebarToggleLabel}
                isSidebarVisible={isSidebarVisible}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={() => setMobileSidebarOpen(false)}
            >
                <div className="text-uppercase text-muted mt-1 mb-1" style={{ fontSize: 11 }}>
                    Área personal
                </div>

                <NavLink
                    to="/portal-emp"
                    end
                    onClick={() => setMobileSidebarOpen(false)}
                    className={({ isActive }) =>
                        "nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
                        (isActive ? "admin-active-link" : "dashboard")
                    }
                >
                    <i className="ri-dashboard-fill icon-part" />
                    <span className="text-part">Panel</span>
                </NavLink>
                <Link
                    to="/"
                    className="nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 home"
                >
                    <i className="ri-home-2-fill icon-part" />
                    <span className="text-part">Inicio</span>
                </Link>

                <div className="text-uppercase text-muted mt-3 mb-1" style={{ fontSize: 11 }}>
                    Gestión
                </div>

                <NavLink
                    to="/portal-emp/cargas"
                    onClick={() => setMobileSidebarOpen(false)}
                    className={({ isActive }) =>
                        "nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
                        (isActive ? "admin-active-link" : "emp")
                    }
                >
                    <i className="ri-upload-cloud-2-fill icon-part" />
                    <span className="text-part">Cargar estudiantes</span>
                </NavLink>

                <NavLink
                    to="/portal-emp/matricula"
                    onClick={() => setMobileSidebarOpen(false)}
                    className={({ isActive }) =>
                        "nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
                        (isActive ? "admin-active-link" : "user")
                    }
                >
                    <i className="ri-user-add-fill icon-part" />
                    <span className="text-part">Matrícula masiva</span>
                </NavLink>

                <NavLink
                    to="/portal-emp/estudiantes"
                    onClick={() => setMobileSidebarOpen(false)}
                    className={({ isActive }) =>
                        "nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
                        (isActive ? "admin-active-link" : "course")
                    }
                >
                    <i className="ri-user-search-fill icon-part" />
                    <span className="text-part">Mis estudiantes</span>
                </NavLink>

                <div className="text-uppercase text-muted mt-3 mb-1" style={{ fontSize: 11 }}>
                    Ayuda
                </div>
                <NavLink
                    to="/portal-emp/capacitacion"
                    onClick={() => setMobileSidebarOpen(false)}
                    className={({ isActive }) =>
                        "nav-link custom-block-link d-flex align-items-stretch p-0 rounded-2 mb-2 " +
                        (isActive ? "admin-active-link" : "instr")
                    }
                >
                    <i className="ri-book-open-fill icon-part" />
                    <span className="text-part">Instructivo</span>
                </NavLink>
            </PortalSidebarShell>

            <main className="flex-grow-1 d-flex flex-column" style={{ minWidth: 0 }}>
                <PortalTopBar
                    sidebarToggleLabel={sidebarToggleLabel}
                    isSidebarVisible={isSidebarVisible}
                    onToggleSidebar={toggleSidebar}
                    userMenuOpen={userMenuOpen}
                    userMenuRef={userMenuRef}
                    onToggleUserMenu={() => setUserMenuOpen((open) => !open)}
                    userAvatarColor="var(--light-blue)"
                    userInitial={userInitial}
                    userDisplayName={companyUserName}
                    profileTo="/portal-emp/perfil-empresa"
                    onCloseUserMenu={() => setUserMenuOpen(false)}
                    onLogout={handleLogout}
                    menuAriaLabel="Opciones de cuenta organización"
                    showHeaderIcons={false}
                />

                <div className="profile-banner-emp">
                    <div className="banner-info-emp">
                        {company ? (
                            <>
                                <h4>{capitalizeFirstLetter(company.company_name)}</h4>
                                <div className="admin-subtittle">
                                    <span>RUT: {formatRut(company.rut) || company.rut}</span>
                                    {company.is_active !== undefined && (
                                        <span> · Activa: {company.is_active ? "Sí" : "No"}</span>
                                    )}
                                </div>
                            </>
                        ) : (
                            <span className="text-muted small">Cargando organización...</span>
                        )}
                    </div>
                </div>

                <Outlet />
                <ClassFooter footerData={footerData} isLoading={footerLoading} />
            </main>
            
        </div>
    );
}